/**
 * F-135 "download this element's file" — the ONE definition of WHAT an
 * element downloads and what the saved file is CALLED, shared by the F-27
 * toolbar's download button (./toolbar.tsx) and the element's context menu
 * (features/elements/ElementsLayer), so the two entry points can never
 * disagree on which elements have a file or how it is named.
 *
 * Two elements carry a file: an image (its asset) and a `file` element (its
 * asset). A link card's thumbnail is the server's re-hosted copy of someone
 * else's page image, not something the user put on the board, and a stroke
 * has no bytes at all — neither is downloadable.
 */
import { withExtensionFor, type ElementInfo } from "@kola/shared";

import { downloadAsset } from "../../../core/download-asset.js";

export interface DownloadTarget {
  assetId: string;
  /** The name the file was uploaded under, when one was recorded. */
  name: string | null;
  /** The recorded content type, when the payload carries it (a `file` element). */
  contentType: string | null;
}

/** What a download of this element saves, or null when it has no file. */
export function downloadTarget(element: ElementInfo): DownloadTarget | null {
  const payload = element.payload;
  if (payload.kind === "image") {
    return { assetId: payload.assetId, name: payload.name ?? null, contentType: null };
  }
  if (payload.kind === "file") {
    return { assetId: payload.assetId, name: payload.name, contentType: payload.contentType };
  }
  return null;
}

/**
 * The saved file's name. An uploaded name wins; an image's is re-extensioned
 * to the bytes actually stored (the client re-encodes big PNG/JPEGs as WebP
 * on the way up, so "photo.jpg" may hold WebP — `withExtensionFor`). A file
 * with no recorded name (an image from before names were kept, a generated
 * thumbnail) is named after its asset id with the extension its type implies.
 * `blobType` is the fetched blob's type — the server's recorded content type.
 */
export function downloadFileName(target: DownloadTarget, blobType: string): string {
  const type = target.contentType ?? blobType;
  if (target.name !== null && target.name.trim() !== "") {
    // A `file` element's bytes are stored untouched, so its name is already
    // right; only an image (re-encoded on upload) needs the extension checked.
    return target.contentType === null ? withExtensionFor(target.name, type) : target.name;
  }
  return withExtensionFor(target.assetId, type);
}

/**
 * Fetch the element's bytes with the caller's identity and hand them to the
 * browser to save (core/download-asset does the fetching and saving — this
 * module only decides WHAT and under what name). Re-reads nothing from the
 * doc — callers pass the element as it is NOW (the pill and the menu both
 * re-resolve by id at click time). Failures surface as a toast; a download is
 * fire-and-forget for the caller.
 */
export async function downloadElementAsset(element: ElementInfo): Promise<void> {
  const target = downloadTarget(element);
  if (target === null) return;
  await downloadAsset(target.assetId, (blobType) => downloadFileName(target, blobType));
}
